import {
    Platform
} from "react-native";
import type {
    ColorAreaStripsConstantType,
    ColorAreaOriginConstantType,
    ColorAreaGuidesConstantType,
    ColorAreaThumbConstantType,
    ColorAreaPointConstantType,
    ColorAreaStepConstantType,
    ColorAreaRingConstantType,
    ColorAreaKeyConstantType,
    ColorAreaStrips,
    ColorAreaOrigin,
    ColorAreaShape,
    ColorAreaValue,
    ColorAreaGuide,
    ColorAreaRow
} from "./type";
import {
    getContrastGuidePoints,
    getMaxChroma,
    getLuminance,
    oklchToHex,
    parseColor
} from "../../helpers/color";
import {
    getHorizontalStep
} from "../../helpers/direction";

export const COLOR_AREA_AXIS_SCALE = 1.08;

export const COLOR_AREA_STRIP_HEIGHT_WEB = 2;

export const COLOR_AREA_STRIP_HEIGHT_NATIVE = 4;

export const COLOR_AREA_STOP_COUNT = 5;

export const COLOR_AREA_HALO_ALPHA = 0.7;

export const COLOR_AREA_CHROMA_STEP = 0.005;

export const COLOR_AREA_CHROMA_PAGE_STEP = 0.05;

export const COLOR_AREA_LIGHTNESS_STEP = 0.01;

export const COLOR_AREA_LIGHTNESS_PAGE_STEP = 0.1;

export const COLOR_AREA_FRACTION_STEP = 0.01;

export const COLOR_AREA_FRACTION_PAGE_STEP = 0.1;

export const COLOR_AREA_EDGE_LIGHTNESS = 0.001;

export const COLOR_AREA_DEFAULT_VALUE: ColorAreaValue = {
    lightness: 0.5,
    chroma: 0
};

export const COLOR_AREA_DEFAULT_GUIDES = [
    4.5
];

export const COLOR_AREA_ASPECT_RATIO = 1.6;

export const COLOR_AREA_NAME_KEYS = {
    "colorName-orange": "colorName-orange",
    "colorName-purple": "colorName-purple",
    "colorName-yellow": "colorName-yellow",
    "colorName-black": "colorName-black",
    "colorName-green": "colorName-green",
    "colorName-white": "colorName-white",
    "colorName-blue": "colorName-blue",
    "colorName-cyan": "colorName-cyan",
    "colorName-gray": "colorName-gray",
    "colorName-pink": "colorName-pink",
    "colorName-teal": "colorName-teal",
    "colorName-red": "colorName-red"
} as const;

const clampNumber = (value: number, min: number, max: number) => {
    return Math.min(max, Math.max(min, value));
};

const roundNumber = (value: number, precision: number) => {
    return Math.round(value * precision) / precision;
};

export const getColorAreaAxisMax = (cuspChroma: number) => {
    return cuspChroma * COLOR_AREA_AXIS_SCALE;
};

export const getColorAreaStripHeight = () => {
    return Platform.OS === "web" ? COLOR_AREA_STRIP_HEIGHT_WEB : COLOR_AREA_STRIP_HEIGHT_NATIVE;
};

export const getColorAreaClampedValue = ({
    lightness,
    chroma,
    hue
}: ColorAreaValue & {
    hue: number;
}): ColorAreaValue => {
    const nextLightness = clampNumber(lightness, 0, 1);

    return {
        chroma: clampNumber(chroma, 0, getMaxChroma(nextLightness, hue)),
        lightness: nextLightness
    };
};

export const getColorAreaFraction = ({
    value,
    hue
}: {
    value: ColorAreaValue;
    hue: number;
}) => {
    const maxChroma = getMaxChroma(value.lightness, hue);

    return maxChroma > 0 ? clampNumber(value.chroma / maxChroma, 0, 1) : 0;
};

const getColorAreaShapeValue = ({
    lightness,
    fraction,
    shape,
    value,
    hue
}: {
    value: ColorAreaValue;
    shape: ColorAreaShape;
    lightness: number;
    fraction: number;
    hue: number;
}): ColorAreaValue => {
    const nextLightness = clampNumber(lightness, 0, 1);

    if(shape === "gamut") {
        return getColorAreaClampedValue({
            lightness: nextLightness,
            chroma: value.chroma,
            hue
        });
    }

    return {
        chroma: clampNumber(fraction, 0, 1) * getMaxChroma(nextLightness, hue),
        lightness: nextLightness
    };
};

export const getColorAreaStrips = ({
    stripHeight,
    axisMax,
    height,
    shape,
    width,
    hue
}: ColorAreaStripsConstantType): ColorAreaStrips => {
    const count = Math.ceil(height / stripHeight);

    if(shape === "square") {
        const squareRows: Array<ColorAreaRow> = [];

        for(let index = 0; index < count; index++) {
            const lightness = 1 - Math.min((index + 0.5) * stripHeight, height) / height;

            const limit = getMaxChroma(lightness, hue);

            const stops = [];

            for(let step = 0; step <= COLOR_AREA_STOP_COUNT; step++) {
                stops.push({
                    color: oklchToHex(lightness, limit * step / COLOR_AREA_STOP_COUNT, hue),
                    offset: step / COLOR_AREA_STOP_COUNT
                });
            }

            squareRows.push({
                y: index * stripHeight,
                width,
                stops
            });
        }

        return {
            polygon: `0,0 ${width},0 ${width},${height} 0,${height}`,
            rows: squareRows,
            boundary: ""
        };
    }

    const edges: Array<number> = [];

    for(let index = 0; index <= count; index++) {
        edges.push(getMaxChroma(1 - Math.min(index * stripHeight, height) / height, hue));
    }

    const rows: Array<ColorAreaRow> = [];

    for(let index = 0; index < count; index++) {
        const lightness = 1 - Math.min((index + 0.5) * stripHeight, height) / height;

        const limit = Math.max(edges[index] ?? 0, edges[index + 1] ?? 0, getMaxChroma(lightness, hue));

        const stops = [];

        for(let step = 0; step <= COLOR_AREA_STOP_COUNT; step++) {
            stops.push({
                color: oklchToHex(lightness, limit * step / COLOR_AREA_STOP_COUNT, hue),
                offset: step / COLOR_AREA_STOP_COUNT
            });
        }

        rows.push({
            width: Math.min(width, limit / axisMax * width + 0.5),
            y: index * stripHeight,
            stops
        });
    }

    const points = edges.map((chroma, index) => `${(chroma / axisMax * width).toFixed(1)},${Math.min(index * stripHeight, height)}`);

    return {
        boundary: points.join(" "),
        polygon: [
            "0,0",
            ...points,
            `0,${height}`
        ].join(" "),
        rows
    };
};

export const getColorAreaGuides = ({
    contrastGuides,
    axisMax,
    surface,
    height,
    alpha,
    shape,
    width,
    hue
}: ColorAreaGuidesConstantType): Array<ColorAreaGuide> => {
    return contrastGuides.map((target) => {
        const guide = getContrastGuidePoints({
            isChromaRelative: shape === "square",
            surface,
            axisMax,
            target,
            height,
            alpha,
            width,
            hue
        });

        return {
            points: guide.points.map((point) => `${point[0].toFixed(1)},${point[1].toFixed(1)}`).join(" "),
            top: guide.top,
            target
        };
    });
};

export const getColorAreaOrigin = ({
    direction,
    pageX,
    pageY,
    width
}: ColorAreaOriginConstantType): ColorAreaOrigin => {
    const isWeb = Platform.OS === "web" && typeof window !== "undefined";

    const scrollX = isWeb ? window.scrollX : 0;

    const scrollY = isWeb ? window.scrollY : 0;

    return {
        x: (direction === "rtl" ? pageX + width : pageX) + scrollX,
        y: pageY + scrollY
    };
};

export const getColorAreaPoint = ({
    direction,
    pointerX,
    pointerY,
    axisMax,
    height,
    origin,
    shape,
    width,
    hue
}: ColorAreaPointConstantType): ColorAreaValue => {
    const offsetX = direction === "rtl" ? origin.x - pointerX : pointerX - origin.x;

    const fractionX = clampNumber(offsetX / width, 0, 1);

    const fractionY = clampNumber((pointerY - origin.y) / height, 0, 1);

    if(shape === "square") {
        const lightness = clampNumber(1 - fractionY, COLOR_AREA_EDGE_LIGHTNESS, 1 - COLOR_AREA_EDGE_LIGHTNESS);

        return {
            chroma: fractionX * getMaxChroma(lightness, hue),
            lightness
        };
    }

    return getColorAreaClampedValue({
        chroma: fractionX * axisMax,
        lightness: 1 - fractionY,
        hue
    });
};

export const getColorAreaStepValue = ({
    direction,
    shape,
    value,
    hue
}: ColorAreaStepConstantType): ColorAreaValue => {
    return getColorAreaShapeValue({
        lightness: roundNumber(value.lightness + direction * COLOR_AREA_LIGHTNESS_STEP, 1000),
        fraction: getColorAreaFraction({
            value,
            hue
        }),
        shape,
        value,
        hue
    });
};

export const getColorAreaKeyValue = ({
    direction,
    shiftKey,
    shape,
    value,
    hue,
    key
}: ColorAreaKeyConstantType): ColorAreaValue | null => {
    const horizontalStep = getHorizontalStep({
        direction,
        key
    });

    const fraction = getColorAreaFraction({
        value,
        hue
    });

    if(horizontalStep && shape === "square") {
        return {
            chroma: clampNumber(roundNumber(fraction + horizontalStep * (shiftKey ? COLOR_AREA_FRACTION_PAGE_STEP : COLOR_AREA_FRACTION_STEP), 1000), 0, 1) * getMaxChroma(value.lightness, hue),
            lightness: value.lightness
        };
    }

    if(horizontalStep) {
        return getColorAreaClampedValue({
            chroma: roundNumber(value.chroma + horizontalStep * (shiftKey ? COLOR_AREA_CHROMA_PAGE_STEP : COLOR_AREA_CHROMA_STEP), 10000),
            lightness: value.lightness,
            hue
        });
    }

    const lightnessStep = shiftKey ? COLOR_AREA_LIGHTNESS_PAGE_STEP : COLOR_AREA_LIGHTNESS_STEP;

    const lightnessSteps: Record<string, number> = {
        PageDown: -COLOR_AREA_LIGHTNESS_PAGE_STEP,
        PageUp: COLOR_AREA_LIGHTNESS_PAGE_STEP,
        ArrowDown: -lightnessStep,
        ArrowUp: lightnessStep
    };

    const step = lightnessSteps[key];

    if(step !== undefined) {
        return getColorAreaShapeValue({
            lightness: roundNumber(value.lightness + step, 1000),
            fraction,
            shape,
            value,
            hue
        });
    }

    if(key === "Home") {
        return {
            lightness: value.lightness,
            chroma: 0
        };
    }

    if(key === "End") {
        return {
            chroma: getMaxChroma(value.lightness, hue),
            lightness: value.lightness
        };
    }

    return null;
};

export const getColorAreaThumb = ({
    axisMax,
    height,
    shape,
    value,
    width,
    hue
}: ColorAreaThumbConstantType) => {
    if(shape === "square") {
        return {
            y: (1 - value.lightness) * height,
            x: getColorAreaFraction({
                value,
                hue
            }) * width
        };
    }

    return {
        x: axisMax > 0 ? value.chroma / axisMax * width : 0,
        y: (1 - value.lightness) * height
    };
};

export const getColorAreaRingColor = ({
    colors
}: ColorAreaRingConstantType) => {
    const light = colors.content.container.default;

    const dark = colors.content.container.inverse;

    const lightRgb = parseColor(light)?.rgb;

    const darkRgb = parseColor(dark)?.rgb;

    if(!lightRgb || !darkRgb) {
        return light;
    }

    return getLuminance(lightRgb) >= getLuminance(darkRgb) ? light : dark;
};
